// src/components/CommandBar.tsx
import React, { useState, useRef, useEffect } from "react";
import { tokens, Button, Input } from "@fluentui/react-components";
import {
  Search20Regular,
  Add20Regular,
  Edit20Regular,
  Delete20Regular,
  Merge20Regular,
  MoreHorizontal20Regular,
} from "@fluentui/react-icons";

export interface CommandBarProps {
  /** Title text shown on the left */
  header: string;
  /** Optional click handlers */
  onSearchClick?: () => void;
  onSearchChange?: (value: string) => void;
  onNewClick?: () => void;
  onEditClick?: () => void;
  onMergeClick?: () => void;
  onDeleteClick?: () => void;
  onDeleteSelected?: () => void; // <--- hinzugefügt!
  /** When false, the Delete button is hidden entirely (used for role-based gating). */
  canDelete?: boolean;
  /** When false, the Edit button is disabled (greyed). Defaults to true so
   *  existing callers that rely on an always-active Edit button are unaffected. */
  canEdit?: boolean;
  /** When false, the Merge button is disabled (greyed). Defaults to false so
   *  callers that don't support merging never show an active Merge button. */
  canMerge?: boolean;
  /** When false, the New button is hidden entirely. */
  canCreate?: boolean;
  /** Compact/mobile layout — lets the header, search and action buttons wrap
   *  to full width instead of overflowing off-screen on narrow viewports. */
  compact?: boolean;
}

/**
 * A full-width command bar with a header, divider, search icon, action buttons, and overflow menu.
 */
export const CommandBar: React.FC<CommandBarProps> = ({
  header,
  onSearchChange,
  onNewClick,
  onEditClick,
  onMergeClick,
  onDeleteClick,
  onDeleteSelected, // <--- hinzugefügt!
  canDelete = true,
  canEdit = true,
  canMerge = false,
  canCreate = true,
  compact = false,
}) => {
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchValue(e.target.value);
    if (onSearchChange) onSearchChange(e.target.value);
  };

  useEffect(() => {
    if (searchOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [searchOpen]);

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        padding: "10px 10px",
        color: "#341c34",
        borderBottom: `3px solid #341c34`,
        ...(compact ? { width: "100%" } : {}),
      }}
    >
      {/* Header + Divider + Search */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "4px",
          justifyContent: "space-between",
          ...(compact ? { width: "100%", flexWrap: "wrap", rowGap: 8 } : {}),
        }}
      >
        <span
          style={{
            fontWeight: 600,
            fontSize: "1.2rem",
            marginRight: 12,
            marginBottom: 5,
          }}
        >
          {header}
        </span>
        {searchOpen ? (
          <Input
            ref={inputRef}
            type="text"
            value={searchValue}
            onChange={handleInputChange}
            onBlur={() => setSearchOpen(false)}
            placeholder="Suchen..."
            style={{
              border: "1px solid #ccc",
              borderRadius: "4px",
              padding: "2px 8px",
              fontSize: "1rem",
              minWidth: "120px",
            }}
          />
        ) : (
          <Search20Regular
            title="Search"
            aria-label="Search"
            onClick={() => setSearchOpen(true)}
            style={{ cursor: "pointer" }}
          />
        )}

        {/* Action buttons */}
        <div style={{ display: "flex", gap: "4px", alignItems: "center" }}>
          {canCreate && (
            <Button
              appearance="transparent"
              icon={<Add20Regular />}
              onClick={onNewClick}
              style={{ color: "#341c34" }}
            >
              Neu
            </Button>
          )}
          {/* Merge button only for callers that wire onMergeClick (ContactTable);
              other tables keep their toolbar unchanged. Disabled (greyed) until
              >1 row is selected. */}
          {onMergeClick && (
            <Button
              appearance="transparent"
              icon={<Merge20Regular />}
              onClick={onMergeClick}
              disabled={!canMerge}
              style={{ color: canMerge ? "#341c34" : undefined }}
            >
              Zusammenführen
            </Button>
          )}
          <Button
            appearance="transparent"
            icon={<Edit20Regular />}
            onClick={onEditClick}
            disabled={!canEdit}
            style={{ color: canEdit ? "#341c34" : undefined }}
          >
            Bearbeiten
          </Button>
          {canDelete && (
            <Button
              appearance="transparent"
              icon={<Delete20Regular />}
              onClick={onDeleteSelected} // <--- geändert!
              style={{ color: "#341c34" }}
            >
              Löschen
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default CommandBar;
